import React, { useEffect, useMemo, useRef, useState } from 'react'
import AsyncSelect from 'react-select/async'
import { useHistory, useLocation } from 'react-router'
import { Button, Card, CardBody, Col, Form, FormGroup, Input, Label, Row, Table } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import SellersApiV2 from 'api/v2/admins/sellers-api-v2'
import { getPhase3ErrorMessage } from '../helpers/phase3-inbound-journey-helper'
import { useAuth } from 'providers/auth-provider'


const formatConsignorOptions = (seller = {}) => {
  const userId = String(seller.user_id || seller.id || '')
  const details = seller.details || []

  if (!details.length) {
    return [ {
      value        : userId,
      userId       : userId,
      userDetailId : '',
      label        : seller.name || seller.email || seller.code || '-',
      email        : seller.email || '',
      code         : seller.code || ''
    } ]
  }

  return details.map((detail) => ({
    value        : `${userId}:${detail.id}`,
    userId       : userId,
    userDetailId : String(detail.id),
    label        : detail.name || seller.name || seller.email || '-',
    email        : seller.email || '',
    code         : seller.code || ''
  }))
}

const formatConsignorOptionLabel = ({ code, label, email }) => (
  <>
    <span className='mr-3'>{ code || '-' }</span>
    <span className='mr-3'>{ label || '-' }</span>
    <span className='text-muted'>{ email || '-' }</span>
  </>
)

const formatProductOption = (product = {}) => ({
  value : String(product.id || ''),
  label : `${product.sku || '-'} — ${product.name || '-'}`,
  sku   : product.sku || '',
  name  : product.name || ''
})

const filenameFromContentDisposition = (contentDisposition = '') => {
  const encodedFilename = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  if (encodedFilename) {
    try {
      return decodeURIComponent(encodedFilename)
    } catch (error) {
      return encodedFilename
    }
  }

  return contentDisposition.match(/filename="?([^";]+)"?/i)?.[1] || ''
}

const fallbackPdfFilename = (tandaMasuk = {}) => {
  const customerName = tandaMasuk.user_detail?.formatted_name ||
    tandaMasuk.user_detail?.name ||
    tandaMasuk.user?.name ||
    tandaMasuk.products?.[0]?.supplier_name ||
    `TandaMasuk${tandaMasuk.id || ''}`
  const safeCustomerName = String(customerName).trim().replace(/\s+/g, '').replace(/[\\/:*?"<>|]/g, '') || 'TandaMasuk'
  const createdAt = tandaMasuk.created_at ? new Date(tandaMasuk.created_at) : new Date()
  const validDate = Number.isNaN(createdAt.getTime()) ? new Date() : createdAt
  const day = String(validDate.getDate()).padStart(2, '0')
  const month = String(validDate.getMonth() + 1).padStart(2, '0')
  const year = validDate.getFullYear()

  return `${safeCustomerName}_${day}${month}${year}.pdf`
}

const buildInitialForm = () => {
  const params = new URLSearchParams(window.location.search)

  return {
    user_id     : params.get('user_id') || '',
    user_detail_id : params.get('user_detail_id') || '',
    product_ids : params.get('product_ids') || '',
    store_id    : params.get('store_id') || '',
    notes       : ''
  }
}

const TANDA_MASUK_PAGE_SIZE = 20

const Phase3TandaMasukPage = ({ pageUtils, apiNamespace = '' }) => {
  const history = useHistory()
  const location = useLocation()
  const { user } = useAuth()
  const [ form, setForm ] = useState(buildInitialForm)
  const [ storeCategories, setStoreCategories ] = useState([])
  const [ rows, setRows ] = useState([])
  const [ searchInput, setSearchInput ] = useState('')
  const [ listFilters, setListFilters ] = useState({ search: '', page: 1 })
  const [ pagination, setPagination ] = useState({ total_count: 0, total_pages: 0 })
  const rowsRequestId = useRef(0)
  const [ specificDate, setSpecificDate ] = useState(() => new URLSearchParams(location.search).get('specific_date') || '')
  const [ isSubmitting, setIsSubmitting ] = useState(false)
  const [ isLoading, setIsLoading ] = useState(false)
  const [ lastCreated, setLastCreated ] = useState(null)
  const [ selectedTandaMasuk, setSelectedTandaMasuk ] = useState(null)
  const [ isDetailLoading, setIsDetailLoading ] = useState(false)
  const [ deletingTandaMasukId, setDeletingTandaMasukId ] = useState(null)
  const [ selectedConsignor, setSelectedConsignor ] = useState(null)
  const [ selectedProducts, setSelectedProducts ] = useState([])
  const appliedSpecificDate = new URLSearchParams(location.search).get('specific_date') || ''
  const selectedTandaMasukId = new URLSearchParams(location.search).get('tanda_masuk_id') || ''

  const tandaMasukDeleteRoles = [ 'owner', 'admin', 'warehouse', 'sales', 'purchasing']
  const normalizedUserRole = user?.role?.toString().trim().toLowerCase().replace(/_/g, ' ')
  const canDeleteTandaMasuk = tandaMasukDeleteRoles.includes(normalizedUserRole)

  const productIdsArray = useMemo(() => {
    return form.product_ids
      .split(/[\n,\s]+/)
      .map((value) => value.trim())
      .filter(Boolean)
  }, [ form.product_ids ])

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const loadConsignorOptions = async (keyword = '') => {
    const response = await SellersApiV2.get({
      additionalFilters: {
        code: keyword
      }
    })

    return (response.data?.sellers || []).flatMap(formatConsignorOptions)
  }

  const loadProductOptions = async (keyword = '') => {
    const response = await InboundJourneyApiV2.getProducts({
      q: keyword,
      page: 1,
      per_page: 20,
      excludeTandaMasukAssigned: true,
      apiNamespace: apiNamespace
    })

    return (response.data?.data || response.data?.products || []).map(formatProductOption)
  }

  const handleChangeConsignor = (option) => {
    setSelectedConsignor(option)
    setForm((prev) => ({
      ...prev,
      user_id        : option?.userId || '',
      user_detail_id : option?.userDetailId || ''
    }))
  }

  const loadInitialConsignor = async () => {
    if (!form.user_id) return

    try {
      const response = await SellersApiV2.show(form.user_id)
      const seller = response.data?.seller || {}
      const options = formatConsignorOptions(seller)
      const selected = options.find((option) => option.userDetailId === String(form.user_detail_id)) || options[0] || null

      setSelectedConsignor(selected)
      if (selected && !form.user_detail_id) setField('user_detail_id', selected.userDetailId)
    } catch (error) {
      // Keep URL values usable even when the display option cannot be loaded.
    }
  }

  const handleChangeProducts = (products) => {
    const nextProducts = products || []
    setSelectedProducts(nextProducts)
    setField('product_ids', nextProducts.map((product) => product.value).join(','))
  }

  const loadStoreCategories = async () => {
    try {
      const response = await InboundJourneyApiV2.getStoreCategories({ apiNamespace })
      setStoreCategories(response.data?.data || [])
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    }
  }

  const tandaMasukDetailPath = (tandaMasuk) => {
    const params = new URLSearchParams(location.search)
    params.set('tanda_masuk_id', tandaMasuk.id)
    if (apiNamespace) params.set('api_namespace', apiNamespace)

    return `${location.pathname}?${params.toString()}`
  }

  const tandaMasukListPath = () => {
    const params = new URLSearchParams(location.search)
    params.delete('tanda_masuk_id')
    if (apiNamespace) params.set('api_namespace', apiNamespace)

    const search = params.toString()
    return `${location.pathname}${search ? `?${search}` : ''}`
  }

  const loadSelectedTandaMasuk = async () => {
    if (!selectedTandaMasukId) return

    setIsDetailLoading(true)
    try {
      const response = await InboundJourneyApiV2.showTandaMasuk({ id: selectedTandaMasukId, apiNamespace })
      setSelectedTandaMasuk(response.data?.data || response.data || null)
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsDetailLoading(false)
    }
  }

  const loadRows = async (date = appliedSpecificDate, search = listFilters.search, page = listFilters.page) => {
    const requestId = ++rowsRequestId.current
    setIsLoading(true)

    try {
      const response = await InboundJourneyApiV2.getTandaMasuks({
        specific_date: date, search, page, per_page: TANDA_MASUK_PAGE_SIZE, apiNamespace
      })
      if (rowsRequestId.current !== requestId) return

      setRows(response.data?.data || [])
      setPagination(response.data?.meta || { total_count: 0, total_pages: 0 })
    } catch (error) {
      if (rowsRequestId.current !== requestId) return
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      if (rowsRequestId.current === requestId) setIsLoading(false)
    }
  }

  useEffect(() => {
    loadStoreCategories()
    loadInitialConsignor()
    loadSelectedTandaMasuk()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      const search = searchInput.trim()
      setListFilters((previous) => previous.search === search ? previous : { search, page: 1 })
    }, 400)

    return () => clearTimeout(timer)
  }, [ searchInput ])

  useEffect(() => {
    setSpecificDate(appliedSpecificDate)
    loadRows(appliedSpecificDate, listFilters.search, listFilters.page)
    return () => { rowsRequestId.current += 1 }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ appliedSpecificDate, listFilters.search, listFilters.page, apiNamespace ])

  const applySearch = () => {
    setListFilters({ search: searchInput.trim(), page: 1 })
  }

  const handleFilterDate = () => {
    const params = new URLSearchParams(location.search)
    const normalizedDate = specificDate.trim()

    if (normalizedDate) {
      params.set('specific_date', normalizedDate)
    } else {
      params.delete('specific_date')
    }

    const nextSearch = params.toString()
    const currentSearch = location.search.replace(/^\?/, '')

    if (nextSearch === currentSearch) {
      if (listFilters.page === 1) {
        loadRows(normalizedDate)
      } else {
        setListFilters((previous) => ({ ...previous, page: 1 }))
      }
      return
    }

    setListFilters((previous) => ({ ...previous, page: 1 }))
    history.push({
      pathname : location.pathname,
      search   : nextSearch ? `?${nextSearch}` : ''
    })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setIsSubmitting(true)
    setLastCreated(null)

    try {
      const response = await InboundJourneyApiV2.createTandaMasuk({
        tanda_masuk: {
          user_id     : form.user_id,
          user_detail_id : form.user_detail_id,
          store_id    : form.store_id,
          notes       : form.notes,
          product_ids : productIdsArray
        },
        apiNamespace: apiNamespace
      })

      const data = response.data?.data || response.data
      setLastCreated(data)
      pageUtils?.setAlertMsg?.('Tanda Masuk created successfully')
      loadRows()
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const downloadPdf = async (tandaMasuk) => {
    const id = tandaMasuk.id

    try {
      const response = await InboundJourneyApiV2.downloadTandaMasukPdf({ id, apiNamespace })
      const blob = new Blob([ response.data ], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      const contentDisposition = response.headers?.['content-disposition'] || ''

      link.href = url
      link.download = filenameFromContentDisposition(contentDisposition) || fallbackPdfFilename(tandaMasuk)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    }
  }

  const handleDeleteTandaMasuk = async (tandaMasuk) => {
    const referenceNumber = tandaMasuk.reference_number || `#${tandaMasuk.id}`
    const confirmed = window.confirm(`Hapus Tanda Masuk ${referenceNumber}? Products tidak akan ikut terhapus.`)
    if (!confirmed) return

    setDeletingTandaMasukId(tandaMasuk.id)
    try {
      await InboundJourneyApiV2.deleteTandaMasuk({ id: tandaMasuk.id, apiNamespace })
      // Refresh server totals and refill the current page after a deletion.
      if (listFilters.page > 1 && rows.length === 1) {
        setListFilters((previous) => ({ ...previous, page: previous.page - 1 }))
      } else {
        loadRows()
      }
      setLastCreated((current) => current?.id === tandaMasuk.id ? null : current)

      if (selectedTandaMasuk?.id === tandaMasuk.id) {
        history.replace(tandaMasukListPath())
        setSelectedTandaMasuk(null)
      }

      pageUtils?.setAlertMsg?.(`Tanda Masuk ${referenceNumber} deleted successfully`)
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setDeletingTandaMasukId(null)
    }
  }

  return (
    <>
      <TitlePage mainTitle='Inbound Journey' subTitle='Tanda Masuk' />

      <Card>
        <CardBody>
          <h4>Create Tanda Masuk</h4>
          <p className='text-muted mb-4'>Gunakan setelah Create Consign agar SA / Product Curator dapat memberikan bukti penerimaan PDF kepada consignor. Satu produk hanya boleh memiliki satu Tanda Masuk; hapus Tanda Masuk lama terlebih dahulu sebelum membuat penggantinya.</p>

          <Form onSubmit={ handleSubmit }>
            <Row form>
              <Col md='3'>
                <FormGroup>
                  <Label>Consignor</Label>
                  <AsyncSelect
                    className='react-select success'
                    classNamePrefix='react-select'
                    cacheOptions
                    defaultOptions
                    value={ selectedConsignor }
                    loadOptions={ loadConsignorOptions }
                    onChange={ handleChangeConsignor }
                    formatOptionLabel={ formatConsignorOptionLabel }
                    placeholder='Cari email atau nama consignor'
                    isClearable
                  />
                </FormGroup>
              </Col>
              <Col md='3'>
              </Col>
              <Col md='3'>
                <FormGroup>
                  <Label>Store / Toko</Label>
                  <Input type='select' value={ form.store_id } onChange={ (e) => setField('store_id', e.target.value) }>
                    <option value=''>Pilih Store</option>
                    { storeCategories.map((storeCategory) => (
                      <option key={ storeCategory.id } value={ storeCategory.id }>{ storeCategory.name }</option>
                    )) }
                  </Input>
                </FormGroup>
              </Col>
              <Col md='3'>
                <FormGroup>
                  <Label>Produk</Label>
                  <AsyncSelect
                    className='react-select success'
                    classNamePrefix='react-select'
                    cacheOptions
                    defaultOptions
                    isMulti
                    value={ selectedProducts }
                    loadOptions={ loadProductOptions }
                    onChange={ handleChangeProducts }
                    placeholder='Cari SKU atau nama produk'
                    noOptionsMessage={ ({ inputValue }) => inputValue ? 'Produk tidak ditemukan.' : 'Ketik SKU atau nama produk.' }
                  />
                  <small className='text-muted d-block'>Selected: { productIdsArray.length }</small>
                  <small className='text-muted'>Product yang sudah memiliki Tanda Masuk tidak ditampilkan. Hapus Tanda Masuk lama terlebih dahulu jika perlu membuat ulang.</small>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Notes</Label>
              <Input type='textarea' value={ form.notes } onChange={ (e) => setField('notes', e.target.value) } />
            </FormGroup>

            <Button color='primary' disabled={ isSubmitting }>
              { isSubmitting ? 'Menyimpan...' : 'Buat Tanda Masuk' }
            </Button>
          </Form>

          { lastCreated && (
            <div className='alert alert-success mt-4'>
              <div className='font-weight-bold'>Tanda Masuk created</div>
              <div>
                Reference Number:{ ' ' }
                <a href={ tandaMasukDetailPath(lastCreated) }>{ lastCreated.reference_number || '-' }</a>
              </div>
              <div>
                Products:{ ' ' }
                { lastCreated.products?.length ? lastCreated.products.map((product) => (
                  <div key={ product.id }>
                    { product.inventory?.id ? (
                      <a href={ `/app/warehouse-team/inventory/${product.inventory.id}` }>{ product.sku || '-' }</a>
                    ) : (product.sku || '-') }
                  </div>
                )) : '-' }
              </div>
              <Button color='success' size='sm' className='mt-2' onClick={ () => downloadPdf(lastCreated) }>
                Download PDF Tanda Masuk
              </Button>
            </div>
          ) }
        </CardBody>
      </Card>

      { (isDetailLoading || selectedTandaMasuk) && (
        <Card className='mt-4'>
          <CardBody>
            { isDetailLoading ? (
              <div className='text-muted'>Memuat detail Tanda Masuk...</div>
            ) : (
              <>
                <div className='d-flex justify-content-between align-items-start mb-3'>
                  <div>
                    <h4 className='mb-1'>Tanda Masuk Detail: { selectedTandaMasuk.reference_number || '-' }</h4>
                    <small className='text-muted'>Reference ini adalah identitas receipt untuk membuka kembali data penerimaan yang sama.</small>
                  </div>
                  <Button color='secondary' outline size='sm' tag='a' href={ tandaMasukListPath() }>Close Detail</Button>
                </div>
                <Row>
                  <Col md='3'>
                    <strong>Consignor</strong>
                    <div>{ selectedTandaMasuk.user_detail?.formatted_name || selectedTandaMasuk.user_detail?.name || selectedTandaMasuk.user?.name || '-' }</div>
                    <small className='text-muted'>{ selectedTandaMasuk.user?.email || '-' }</small>
                  </Col>
                  <Col md='3'><strong>Store</strong><div>{ selectedTandaMasuk.store_category?.name || '-' }</div></Col>
                  <Col md='3'><strong>Created By</strong><div>{ selectedTandaMasuk.masuk_by?.name || selectedTandaMasuk.masuk_by?.email || '-' }</div></Col>
                  <Col md='3'><strong>Created At</strong><div>{ selectedTandaMasuk.created_at ? new Date(selectedTandaMasuk.created_at).toLocaleString() : '-' }</div></Col>
                </Row>
                <div className='mt-3'>
                  <strong>Products</strong>
                  { selectedTandaMasuk.products?.length ? selectedTandaMasuk.products.map((product) => (
                    <div key={ product.id }>
                      { product.inventory?.id ? (
                        <a href={ `/app/warehouse-team/inventory/${product.inventory.id}` }>
                          { product.sku || '-' } — { product.name || '-' }
                        </a>
                      ) : `${product.sku || '-'} — ${product.name || '-'}` }
                    </div>
                  )) : <div>-</div> }
                </div>
                <div className='mt-3'><strong>Notes</strong><div>{ selectedTandaMasuk.notes || '-' }</div></div>
                <div className='mt-3'>
                  <Button color='success' size='sm' onClick={ () => downloadPdf(selectedTandaMasuk) }>
                    Download PDF
                  </Button>
                  { canDeleteTandaMasuk && (
                    <Button
                      color='danger'
                      outline
                      size='sm'
                      className='ml-2'
                      disabled={ deletingTandaMasukId === selectedTandaMasuk.id }
                      onClick={ () => handleDeleteTandaMasuk(selectedTandaMasuk) }
                    >
                      { deletingTandaMasukId === selectedTandaMasuk.id ? 'Menghapus...' : 'Hapus Tanda Masuk' }
                    </Button>
                  ) }
                </div>
              </>
            ) }
          </CardBody>
        </Card>
      ) }

      <Card className='mt-4'>
        <CardBody>
          <div className='d-flex justify-content-between align-items-end mb-3'>
            <div>
              <h4 className='mb-1'>Daftar Tanda Masuk</h4>
              <small className='text-muted'>Alurnya sama seperti menu Tanda Masuk di dashboard lama.</small>
            </div>
            <div className='d-flex align-items-end'>
              <FormGroup className='mb-0 mr-2'>
                <Label>Filter berdasarkan Tanggal</Label>
                <Input type='date' value={ specificDate } onChange={ (e) => setSpecificDate(e.target.value) } />
              </FormGroup>
              <Button type='button' color='secondary' onClick={ handleFilterDate } disabled={ isLoading }>
                Filter
              </Button>
            </div>
          </div>

          <div className='d-flex flex-wrap align-items-end justify-content-between mb-3'>
            <FormGroup className='mb-2 mr-3 flex-grow-1' style={ { maxWidth: '540px' } }>
              <Label for='tanda-masuk-global-search'>Global Search</Label>
              <div className='d-flex'>
                <Input
                  id='tanda-masuk-global-search'
                  type='search'
                  value={ searchInput }
                  maxLength={ 100 }
                  onChange={ (event) => setSearchInput(event.target.value) }
                  onKeyDown={ (event) => { if (event.key === 'Enter') applySearch() } }
                  placeholder='Cari SKU, reference MSK, atau nama consignor...'
                  aria-label='Search Tanda Masuk'
                />
                <Button type='button' color='primary' className='ml-2' onClick={ applySearch }>
                  Search
                </Button>
                { searchInput && (
                  <Button type='button' color='secondary' outline className='ml-2' onClick={ () => setSearchInput('') }>
                    Clear
                  </Button>
                ) }
              </div>
            </FormGroup>
            <small className='text-muted mb-2'>
              { isLoading ? 'Searching...' : `${pagination.total_count} Tanda Masuk found` }
            </small>
          </div>

          <Table responsive hover>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Consignor</th>
                <th>Products</th>
                <th>Store</th>
                <th>Created At</th>
                <th className='text-right'>Action</th>
              </tr>
            </thead>
            <tbody>
              { !isLoading && rows.map((row) => (
                <tr key={ row.id }>
                  <td><a className='font-weight-bold' href={ tandaMasukDetailPath(row) }>{ row.reference_number || '-' }</a></td>
                  <td>{ row.user_detail?.formatted_name || row.user_detail?.name || row.user?.name || row.products?.[0]?.supplier_name || '-' }</td>
                  <td>
                    { row.products?.length ? row.products.map((product) => (
                      <div key={ product.id }>
                        { product.inventory?.id ? (
                      <a href={ `/app/warehouse-team/inventory/${product.inventory.id}` }>{ product.sku || '-' }</a>
                    ) : (product.sku || '-') }
                      </div>
                    )) : '-' }
                  </td>
                  <td>{ row.store_category?.name || '-' }</td>
                  <td>{ row.created_at ? new Date(row.created_at).toLocaleString() : '-' }</td>
                  <td className='text-right'>
                    <Button color='success' size='sm' onClick={ () => downloadPdf(row) }>
                      Download PDF
                    </Button>
                    { canDeleteTandaMasuk && (
                      <Button
                        color='danger'
                        outline
                        size='sm'
                        className='ml-2'
                        disabled={ deletingTandaMasukId === row.id }
                        onClick={ () => handleDeleteTandaMasuk(row) }
                      >
                        { deletingTandaMasukId === row.id ? 'Menghapus...' : 'Hapus Tanda Masuk' }
                      </Button>
                    ) }
                  </td>
                </tr>
              )) }
              { isLoading && (
                <tr><td colSpan='6' className='text-center text-muted'>Loading Tanda Masuk...</td></tr>
              ) }
              { !isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan='6' className='text-center text-muted'>
                    { listFilters.search ? 'No matching Tanda Masuk found.' : 'Tanda Masuk tidak ditemukan.' }
                  </td>
                </tr>
              ) }
            </tbody>
          </Table>
          <div className='d-flex flex-wrap justify-content-between align-items-center mt-3'>
            <small className='text-muted'>
              Page { listFilters.page } of { Math.max(1, pagination.total_pages || 0) }
            </small>
            <div>
              <Button
                type='button'
                size='sm'
                color='secondary'
                outline
                className='mr-2'
                disabled={ isLoading || listFilters.page <= 1 }
                onClick={ () => setListFilters((previous) => ({ ...previous, page: previous.page - 1 })) }
              >
                Previous
              </Button>
              <Button
                type='button'
                size='sm'
                color='secondary'
                outline
                disabled={ isLoading || listFilters.page >= (pagination.total_pages || 1) }
                onClick={ () => setListFilters((previous) => ({ ...previous, page: previous.page + 1 })) }
              >
                Next
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

export default Phase3TandaMasukPage
